import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {
  FirePartitionTypeName,
  MATERIAL_STATUSES,
  MaterialSpecification,
  MaterialSpecificationType,
  MaterialStatusName,
  MaterialTypesModel,
  sortedValues
} from "../../core/models/firepartition.type";
import {ApiService} from "../../core/api/api.service";
import {DataHelperService} from "../../shared/helpers/data-helper.service";
import {firstValueFrom} from "rxjs";

@Component({
    selector: 'bergh-edit-material-modal',
    templateUrl: './edit-material-modal.component.html',
    styleUrls: ['./edit-material-modal.component.scss'],
    standalone: false
})
export class EditMaterialModalComponent implements OnInit {
  @Output() cancel: EventEmitter<void> = new EventEmitter();
  @Output() save: EventEmitter<any> = new EventEmitter();
  @Input() material: any | null;
  @Input() materialPosition: number;
  @Input() materialTypes: MaterialTypesModel[];
  @Input() firePartitionType: FirePartitionTypeName;

  public materialForm: UntypedFormGroup;
  materialSpecifications: MaterialSpecification[] | null = null;
  materialSpecificationType: MaterialSpecificationType | null = null
  materialStatuses = sortedValues(MATERIAL_STATUSES);
  isSubmittedForm: boolean = false;

  constructor(
    private formFB: UntypedFormBuilder,
    private _api: ApiService,
    private _dataHelper: DataHelperService
  ) {
  }

  ngOnInit(): void {
    this.loadMaterialForm()
  }

  loadMaterialForm() {
    this.materialForm = this.formFB.group({
      materialPosition: [this.materialPosition],
      materialTypeId: [this.material?.materialTypeId || "", Validators.required],
      materialSpecificationId: [this.material?.materialSpecificationId || ''],
      materialSpecificationText: [this.material?.materialSpecificationText],
      materialSpecificationBrand: [this.material?.materialSpecificationBrand],
      materialRemarks: [this.material?.materialRemarks],
      materialStatus: [this.material?.materialStatus || MaterialStatusName.APPROVED],
      materialPlacedDate: [this.material?.materialPlacedDate],
      materialMaintenanceDate: [this.material?.materialMaintenanceDate],
      materialMaintenanceIntervalMonths: [this.material?.materialMaintenanceIntervalMonths || 12],
      noOfUnits: [this.material?.noOfUnits || 1, [Validators.required, Validators.min(1)]]
    });
    if (this.material) {
      void this.setMaterialType(this.material.materialTypeId)
    }
  }

  onChangeMaterialType(materialTypeId: string) {
    const selectedMaterialType = this.setMaterialType(materialTypeId)
    if (selectedMaterialType) {
      // reset previous values
      this.materialForm.get('materialSpecificationText').setValue(null);
      this.materialForm.get('materialSpecificationBrand').setValue(null);
      this.materialForm.get('materialSpecificationId').setValue(null);
    }
  }

  async setMaterialType(materialTypeId: string) {
    const selectedMaterialType = this.materialTypes.find((type) => type._id === materialTypeId);
    if (selectedMaterialType) {
      this.materialSpecificationType = selectedMaterialType.specificationType;
      if (this.materialSpecificationType === 'LIST') {
        this.materialSpecifications = await firstValueFrom(this._api.request("getMaterialSpecifications", selectedMaterialType._id))
        this.materialForm.get('materialSpecificationId').setValidators(Validators.required);
      } else {
        this.materialSpecifications = null;
        this.materialForm.get('materialSpecificationId').clearValidators();
      }
      this.materialForm.get('materialSpecificationId').updateValueAndValidity();
    }
    return selectedMaterialType
  }

  onSubmit() {
    if (this.materialForm.valid) {
      const materialData = this.materialForm.getRawValue();
      let materialSpecificationName = ''
      if (this.materialSpecificationType === 'LIST' && this.materialSpecifications) {
        materialSpecificationName = this.materialSpecifications.find(spec => spec._id === materialData.materialSpecificationId)?.name
      }
      this.save.emit({...materialData, materialSpecificationName});
      this.isSubmittedForm = false;
      this.materialForm.reset();
      this.loadMaterialForm();
    } else {
      this.isSubmittedForm = true;
    }
  }

  onCancel() {
    this.cancel.emit();
    this.isSubmittedForm = false;
    this.materialForm.reset();
    this.loadMaterialForm();
  }

  restrictDecimals(e) {
    return this._dataHelper.checkIsNumber(e);
  }

  protected readonly FirePartitionTypeName = FirePartitionTypeName;
}
